import React, { useState, useRef, useEffect, useMemo } from "react";
import { Table, Input, Space, Button, InputRef, message, Form } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import Loader from "../GlobalLoader/Loader";
import {
  FilterConfirmProps,
  FilterDropdownProps,
} from "antd/lib/table/interface";
import UserManagementFunction from "../../Functions/UserManagement/UserManagement";
import styles from "./UserLicensesTable.module.scss";
import { useSelector } from "react-redux";
import type { TablePaginationConfig } from "antd/es/table";
import { RootState } from "../../Redux/store";
import { useLicenseSettings } from "./LicenseSettingsContext";
import {
  activateUserFlow,
  deactivateUserFlow,
} from "./utils/LicenseManipulation";
import {
  showExceededLicenseModal,
  showUCaaSAndSMSConfirm,
} from "./modals/modals";
import { getColumns, LicenseType } from "./constants/userLicenses";
import {
  DataIndex,
  DataType,
  FilterParams,
  UserLicenseContext,
  UserLicensesTableProps,
} from "./utils/types";

const UserLicensesTable: React.FC<UserLicensesTableProps> = ({
  currentCustomerLicenseId,
  isVisible,
  value,
  type,
  onLicenseTransfer,
  isAiUsersLicense = false,
}) => {
  const [userLicenses, setUserLicenses] = useState<DataType[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingNotify, setLoadingNotify] = useState<{
    [key: string]: boolean;
  }>({});
  const [loadingResetPassword, setLoadingResetPassword] = useState<{
    [key: string]: boolean;
  }>({});
  const custId = useSelector((state: RootState) => state.user?.user?.custID);
  const searchInput = useRef<InputRef>(null);
  const accessToken = useSelector(
    (state: RootState) => state.access.accessToken
  );
  const customer = useSelector((state: RootState) => state.customer);

  const sessionToken = useSelector((state: RootState) => state.user.token);
  const AuthTokens = useMemo(
    () => ({
      accessToken: accessToken ?? "",
      sessionToken: sessionToken ?? "",
    }),
    [accessToken, sessionToken]
  );
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 5,
    pageSizeOptions: ["5", "20"],
    position: ["bottomCenter"],
  } as TablePaginationConfig);
  const { updateTotalValue, totalValue } = useLicenseSettings();
  const [filterData, setFilterData] = useState<FilterParams>({});
  const [activeFilter, setActiveFilter] = useState<DataIndex | null>(null);

  const [form] = Form.useForm();
  const readOnlyView = isAiUsersLicense;
  const context: UserLicenseContext = {
    currentCustomerLicenseId,
    setUserLicenses,
    updateTotalValue,
    form,
    onLicenseTransfer,
    setLoading,
    AuthTokens,
  };

  useEffect(() => {
    async function fetchData() {
      if (!accessToken || !sessionToken || !custId) {
        return;
      }
      try {
        setLoading(true);
        const baseParams = {
          custId: custId.toString(),
          customerLicenseId: currentCustomerLicenseId.toString(),
          page: pagination.current?.toString(),
          pageSize: pagination.pageSize?.toString(),
          status: readOnlyView ? "ACTIVE" : undefined,
          ...filterData,
        };
        const response =
          await UserManagementFunction.getAllUserLicensesForCustomer(
            accessToken,
            sessionToken,
            baseParams
          );

        const rawLicenses =
          response?.data?.userLicenses ?? [];
        const normalizedLicenses: DataType[] = Array.isArray(rawLicenses)
          ? rawLicenses
          : response?.data?.userLicenses ?? [];
        const filteredLicenses = readOnlyView
          ? normalizedLicenses.filter((license) => !!license.customerLicenseId)
          : normalizedLicenses;

        setUserLicenses(filteredLicenses);
        setPagination((prev) => ({
          ...prev,
          total: response?.data?.totalRows ?? filteredLicenses.length,
        }));
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    }
    if (isVisible) {
      fetchData();
    }
  }, [
    accessToken,
    sessionToken,
    custId,
    isVisible,
    readOnlyView,
    currentCustomerLicenseId,
    JSON.stringify(filterData),
    JSON.stringify(pagination),
  ]);

  const handleChange = (nextPagination: TablePaginationConfig) => {
    setPagination((prev) => ({ ...prev, ...nextPagination }));
  };

  const handleSearch = (
    selectedKeys: string[],
    confirm: FilterDropdownProps["confirm"],
    dataIndex: DataIndex
  ) => {
    confirm();
    const searchValue = selectedKeys[0];
    setActiveFilter(null);

    if (searchValue) {
      setFilterData({ ...filterData, [dataIndex]: searchValue });
    } else {
      const newFilterData = { ...filterData };
      delete newFilterData[dataIndex];
      setFilterData(newFilterData);
    }
  };

  const handleReset = (
    clearFilters: () => void,
    dataIndex: DataIndex,
    confirm: (param?: FilterConfirmProps) => void
  ) => {
    delete filterData[dataIndex];
    clearFilters();
    confirm();
    setActiveFilter(null);
  };

  const handleNotify = async (userId: string) => {
    if (readOnlyView) {
      return;
    }
    if (!accessToken || !sessionToken) {
      message.error("Failed to send credentials. Please login again!", 3);
      return;
    }
    setLoadingNotify((prevState) => ({ ...prevState, [userId]: true }));
    UserManagementFunction.sendCredentialsEmail(accessToken, sessionToken, {
      userId: userId,
    })
      .then((response) => {
        if (response.status) {
          message.success("Credentials sent sucesfully", 3);
        } else {
          message.error("Failed to send credentials", 3);
          console.log(response.message);
        }
      })
      .catch((error) => {
        message.error("Failed to send credentials", 3);
        console.error(error);
      })
      .finally(() => {
        setLoadingNotify((prevState) => ({ ...prevState, [userId]: false }));
      });
  };

  const handleResetPassword = async (userId: string) => {
    if (readOnlyView) {
      return;
    }
    if (!accessToken || !sessionToken) {
      message.error("Failed to reset password. Please login again!", 3);
      return;
    }
    setLoadingResetPassword((prevState) => ({
      ...prevState,
      [userId]: true,
    }));
    UserManagementFunction.resetStrettoPassword(accessToken, sessionToken, {
      userId,
    })
      .then((response) => {
        if (response.status) {
          message.success("Password reset successfully", 3);
        } else {
          message.error("Failed to reset password", 3);
          console.log(response.message);
        }
      })
      .catch((error) => {
        message.error("Failed to reset password", 3);
        console.error(error);
      })
      .finally(() => {
        setLoadingResetPassword((prevState) => ({
          ...prevState,
          [userId]: false,
        }));
      });
  };

  const getColumnSearchProps = (dataIndex: DataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }: any) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Search ${dataIndex
            .replace(/([A-Z])/g, " $1")
            .replace(/^./, (str) => str.toUpperCase())}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ width: 188, marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() =>
              clearFilters && handleReset(clearFilters, dataIndex, confirm)
            }
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: any) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilterDropdownOpenChange: (visible: any) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
        setActiveFilter(dataIndex);
      } else {
        setActiveFilter(null);
      }
    },
    filterDropdownVisible: activeFilter === dataIndex,
  });

  const handleChangeSMS = async (userId: string, userSMSEnabled: boolean) => {
    if (readOnlyView) {
      return;
    }
    try {
      const user = userLicenses.find((user) => user.id === userId);
      if (
        type === LicenseType.UCaaSEnterprise &&
        !userSMSEnabled &&
        !user?.customerLicenseId
      ) {
        showUCaaSAndSMSConfirm(
          (user?.firstName ?? "") + " " + (user?.lastName ?? ""),
          userId,
          context,
          type
        );
        return;
      } else {
        if (!accessToken || !sessionToken) {
          message.error("Failed to update SMS enabled status", 3);
          return;
        }
        setLoading(true);
        const response = await UserManagementFunction.updateSMSLicense(
          accessToken,
          sessionToken,
          {
            customerLicenseId: currentCustomerLicenseId,
            userId: Number(userId),
            SMSEnabled: !userSMSEnabled,
          }
        );
        if (response.status) {
          setUserLicenses((prev) =>
            prev.map((u) =>
              u.id === userId ? { ...u, userSMSEnabled: !userSMSEnabled } : u
            )
          );
          message.success("SMS status updated successfully", 3);
        } else {
          message.error(
            response.message.charAt(0).toUpperCase() +
              response.message.slice(1),
            3
          );
        }
      }
    } catch (error) {
      message.error("Failed to update SMS enabled status", 3);
    }

    setLoading(false);
  };

  const handleToggle = async (userId: string, SMSEnabled: boolean) => {
    if (readOnlyView) {
      return;
    }
    setLoading(true);
    const user = userLicenses.find((user) => user.id === userId);

    if (!user) {
      console.error("User not found");
      setLoading(false);
      return;
    }

    const action = user.customerLicenseId ? "deactivate" : "activate";

    if (action === "activate") {
      await activateUserFlow(
        user,
        userId,
        totalValue,
        value,
        type,
        showExceededLicenseModal,
        context,
        customer.autoMailEnabled
      );
    } else {
      await deactivateUserFlow(user, userId, SMSEnabled, type, context);
    }

    setLoading(false);
  };

  const columns = getColumns(
    getColumnSearchProps,
    type,
    handleToggle,
    handleChangeSMS,
    handleNotify,
    loadingNotify,
    handleResetPassword,
    loadingResetPassword,
    userLicenses,
    { readOnly: readOnlyView }
  );

  return (
    <div className={styles.userLicensesTableContainer}>
      <Table
        columns={columns}
        dataSource={userLicenses}
        pagination={pagination}
        onChange={handleChange}
        scroll={{ y: 400 }}
        locale={{
          emptyText: "No active licenses",
        }}
        loading={{
          spinning: loading,
          indicator: (
            <div>
              <Loader blue={true} size={"sm"} backgroundWeb={false} />
            </div>
          ),
        }}
      ></Table>
    </div>
  );
};

export default UserLicensesTable;
